import { useCallback, useContext, useEffect, useState } from 'react';
import useSearchURLParams, { URLParamsName } from 'hooks/useSearchURLParams';
import useAppQuery from '../../../hooks/useAppQuery';
import useAppUser from '../../../hooks/useAppUser';
import {
  GetLeadRelatedThreadsResponseTO,
  GetThreadParticipantBestProfileResponseTO,
  GetThreadResponseTO,
  GetThreadsResponseTO,
  Thread,
} from '../../../models/inbox/Threads';
import API from '../../../services/API';
import { ThreadNewMessagesData } from '../Inbox.types';
import InboxContext from '../InboxContext';
import { UserTypeToThreadParticipantTypeMapping } from './InboxThread.constants';
import {
  isAllowedCommunicationThread,
  sortThreadsByUpdateDateIfApplicable,
} from './InboxThreads.utils';
import useInboxThreadFilterParams from './useInboxThreadFilterParams';

// @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
export const getThreadInterlocutorUid = ({ thread, userType }) => {
  const currentUserParticipantType =
    // @ts-expect-error TS7053 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    UserTypeToThreadParticipantTypeMapping[userType];

  const interlocutor = thread.participants.find(
    // @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    ({ participantType }) => participantType !== currentUserParticipantType,
  );
  return interlocutor?.uid;
};

// @ts-expect-error TS7031 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
const getRequestedThreadUid = async ({ leadUidParam, threadUidParam }) => {
  if (leadUidParam) {
    try {
      const leadResponse = await API.get<GetLeadRelatedThreadsResponseTO>(
        `/v3/lead-related-threads?leadUid=${leadUidParam}`,
      );

      if (leadResponse?.data?.threadRelatedLeads?.length) {
        const { threadUid } = leadResponse.data.threadRelatedLeads[0];
        return threadUid;
      }
    } catch (err) {
      return null;
    }
  }

  return threadUidParam;
};

const getThreadByUid = async (threadUid: string) => {
  const threadResponse = await API.get<GetThreadResponseTO>(
    `/v3/threads/${threadUid}`,
  );

  return threadResponse?.data?.thread;
};

const limit = 20;

const getLimitForFetching = () => {
  return limit + 1; // increase the limit by one in order to determine if there are more threads
};

const areMoreThreadsAvailable = (fetchedThreads?: Thread[]) => {
  // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const moreThreadsAvailable = fetchedThreads?.length > limit;
  if (moreThreadsAvailable) {
    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    fetchedThreads.splice(limit, 1); // not the nicest solution, should be possibly replaced with using total number of threads returned from the backend (not implemented there yet)
  }
  return moreThreadsAvailable;
};

const updateThreadLastUpdate =
  (threadsWithNewMessages: ThreadNewMessagesData[]) =>
  (thread: Thread): Thread => {
    const threadWithNewMessages = threadsWithNewMessages.find(
      ({ threadUid }) => thread.uid === threadUid,
    );
    const hasNewMessages = !!threadWithNewMessages;

    return hasNewMessages
      ? {
          ...thread,
          lastUpdateDateObj: new Date(),
          participantsReadStatuses:
            threadWithNewMessages.participantsReadStatuses,
        }
      : { ...thread };
  };

const useInboxThreads: () => {
  isLoading: boolean;
  isLoadingMoreThreads: boolean;
  hasMore: boolean;
  loadMore: () => void;
  threads: Thread[];
} = () => {
  const { getParameter, setParameter } = useSearchURLParams();
  const threadUidFromUrl = getParameter(URLParamsName.THREAD_UID);
  const [
    isThreadParticipantBestProfilesUpdating,
    setIsThreadParticipantBestProfilesUpdating,
  ] = useState(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [threadsForBestProfilesFetch, setThreadsForBestProfilesFetch] =
    useState<Thread[]>();
  const { userType } = useAppUser();
  const {
    activeThread,
    threadParticipantBestProfilesMap,
    threadsWithNewMessages,
    setThreadsWithNewMessages,
    getInitialSearchParams,
    updateActiveThreadData,
    updateThreadParticipantBestProfilesMap,
    updateActiveMessageThreadUid,
  } = useContext(InboxContext);
  const { leadUid, params, sortBy } = useInboxThreadFilterParams();

  const { data: threadsData, isLoading: isThreadsLoading } = useAppQuery(
    ['threads', leadUid, params],
    async () => {
      setOffset(0);

      if (leadUid) {
        const leadResponse = await API.get<GetLeadRelatedThreadsResponseTO>(
          `/v3/lead-related-threads?leadUid=${leadUid}`,
        );

        if (leadResponse?.data?.threadRelatedLeads?.length) {
          const { threadUid } = leadResponse.data.threadRelatedLeads[0];
          const thread = await getThreadByUid(threadUid);

          return thread ? { fetchedThreads: [thread] } : { fetchedThreads: [] };
        }

        return { fetchedThreads: [] };
      }

      const response = await API.get<GetThreadsResponseTO>('/v3/threads', {
        params: {
          ...params,
          guestAndAgencyCommunicationOnly: true,
          limit: getLimitForFetching(),
          offset: 0,
        },
      });
      const fetchedThreads = response.data.threads;

      const { leadUidParam, threadUidParam } = getInitialSearchParams();
      const requestedThreadUid = await getRequestedThreadUid({
        leadUidParam,
        threadUidParam,
      });

      if (
        requestedThreadUid &&
        !fetchedThreads.find(({ uid }) => uid === requestedThreadUid)
      ) {
        const threadResponse = await API.get<GetThreadResponseTO>(
          `/v3/threads/${requestedThreadUid}`,
        );

        if (threadResponse?.data?.thread) {
          fetchedThreads.unshift(threadResponse.data.thread);
        }
      }

      return {
        requestedLeadUid: requestedThreadUid && leadUidParam,
        requestedThreadUid,
        fetchedThreads,
      };
    },
    { enabled: !!leadUid || !!params },
  );

  const { data: moreThreads, isInitialLoading: isLoadingMoreThreads } =
    useAppQuery(
      ['moreThreads', offset],
      async () => {
        const response = await API.get<GetThreadsResponseTO>('/v3/threads', {
          params: {
            ...params,
            guestAndAgencyCommunicationOnly: true,
            limit: getLimitForFetching(),
            offset,
          },
        });

        return response.data.threads;
      },
      { enabled: offset > 0 },
    );

  const {
    data: bestProfiles,
    isInitialLoading: isThreadParticipantBestProfilesLoading,
  } = useAppQuery(
    ['bestProfiles', threadsForBestProfilesFetch],
    async () => {
      setIsThreadParticipantBestProfilesUpdating(true);
      let fetchedBestProfiles;
      await Promise.all(
        // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        threadsForBestProfilesFetch
          .filter(({ uid }) => !threadParticipantBestProfilesMap[uid])
          .map(async (thread) => {
            const interlocutorUid = getThreadInterlocutorUid({
              thread,
              userType,
            });
            const response =
              await API.get<GetThreadParticipantBestProfileResponseTO>(
                `/v3/thread-participant-best-profile/${interlocutorUid}`,
              );

            return {
              threadUid: thread.uid,
              bestProfile: response.data.bestProfile,
            };
          }),
      ).then((profiles) => {
        fetchedBestProfiles = profiles.reduce(
          (accumulator, { threadUid, bestProfile }) => {
            return {
              ...accumulator,
              [threadUid]: bestProfile,
            };
          },
          {},
        );
      });
      return fetchedBestProfiles;
    },
    { enabled: !!threadsForBestProfilesFetch },
  );

  useEffect(() => {
    const { fetchedThreads, requestedLeadUid, requestedThreadUid } =
      threadsData || {};

    if (fetchedThreads?.length) {
      setThreadsForBestProfilesFetch(fetchedThreads);

      const nextActiveThread =
        (requestedThreadUid &&
          fetchedThreads.find(({ uid }) => uid === requestedThreadUid)) ||
        fetchedThreads[0];
      const nextActiveThreadLeadUid = leadUid || requestedLeadUid;

      updateActiveThreadData({
        activeThread: nextActiveThread,
        activeThreadLeadUid: nextActiveThreadLeadUid,
      });

      // Adjust query params for the initial page load (/inbox without query params) or for a redirect from elsewhere.
      if (
        nextActiveThread?.uid &&
        getParameter(URLParamsName.THREAD_UID) !== nextActiveThread.uid
      ) {
        setParameter(URLParamsName.THREAD_UID, nextActiveThread.uid, {
          replace: true,
        });

        const leadUidFromUrl = getParameter(URLParamsName.LEAD_UID);

        if (
          nextActiveThreadLeadUid &&
          nextActiveThreadLeadUid !== leadUidFromUrl
        ) {
          setParameter(URLParamsName.LEAD_UID, nextActiveThreadLeadUid, {
            replace: true,
          });
        }
      }
    } else {
      updateActiveThreadData({});
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      updateActiveMessageThreadUid(null);
    }

    const moreThreadsAvailable = areMoreThreadsAvailable(fetchedThreads);
    setThreads(fetchedThreads || []);
    setHasMore(moreThreadsAvailable);
  }, [threadsData]);

  useEffect(() => {
    if (bestProfiles) {
      updateThreadParticipantBestProfilesMap(bestProfiles);
      setIsThreadParticipantBestProfilesUpdating(false);
    }
  }, [bestProfiles]);

  useEffect(() => {
    if (moreThreads) {
      setThreadsForBestProfilesFetch(moreThreads);
      const moreThreadsAvailable = areMoreThreadsAvailable(moreThreads);
      setThreads((currentThreads) => [...currentThreads, ...moreThreads]);
      setHasMore(moreThreadsAvailable);
    }
  }, [moreThreads]);

  useEffect(() => {
    let ignore = false;

    if (threadsWithNewMessages?.length) {
      Promise.all(
        threadsWithNewMessages
          .filter(({ threadUid }) => !threads.some((t) => t.uid === threadUid))
          .map(({ threadUid }) => getThreadByUid(threadUid)),
      ).then((newThreadsRaw) => {
        if (ignore) {
          return;
        }

        const newThreads = newThreadsRaw.filter(
          (t): t is Thread => t && isAllowedCommunicationThread(t),
        );

        setThreads((currentThreads) => {
          const nextThreads = [...newThreads.reverse(), ...currentThreads].map(
            updateThreadLastUpdate(threadsWithNewMessages),
          );
          sortThreadsByUpdateDateIfApplicable(nextThreads, sortBy);
          return nextThreads;
        });

        setThreadsForBestProfilesFetch((old) => [
          ...(old ?? []),
          ...newThreads,
        ]);
        setThreadsWithNewMessages(null);
      });
    }

    return () => {
      ignore = true;
    };
  }, [threads, threadsWithNewMessages]);

  useEffect(() => {
    // When changing from thread to thread by clicking in the left panel
    if (threadUidFromUrl && activeThread?.uid !== threadUidFromUrl) {
      const nextActiveThread = threads.find(
        ({ uid }) => threadUidFromUrl === uid,
      );

      if (nextActiveThread) {
        updateActiveThreadData({ activeThread: nextActiveThread });
      }
    }
  }, [activeThread, threadUidFromUrl, threads]);

  const loadMore = useCallback(() => {
    setOffset((currentOffset) => currentOffset + limit);
  }, [offset]);

  return {
    isLoading:
      !offset &&
      (isThreadsLoading ||
        isThreadParticipantBestProfilesLoading ||
        isThreadParticipantBestProfilesUpdating),
    isLoadingMoreThreads:
      !!offset &&
      (isLoadingMoreThreads ||
        isThreadParticipantBestProfilesLoading ||
        isThreadParticipantBestProfilesUpdating),
    hasMore,
    loadMore,
    threads,
  };
};

export default useInboxThreads;
